import { useMemo } from "react";
import { useAuth } from "@/lib/auth-context";
import * as api from "@/lib/api";
import { usePinFilters } from "@/lib/pins-context";
import { useImpersonation } from "@/lib/impersonation-context";
import { PinFilterBar } from "@/components/pin-filter-bar";
import { UserManagementPanel, ALL_ROLE_OPTIONS } from "@/components/user-management-panel";
import type { UserListItem } from "@dugads/shared";

export default function UsersPage() {
  const { user: authUser } = useAuth();
  const { activeOrgIds, activeSchoolIds } = usePinFilters();
  const { canImpersonate, addPersona, isPersonaSaved } = useImpersonation();
  const hasPinFilter = activeOrgIds.length > 0 || activeSchoolIds.length > 0;

  const isSuperAdmin = authUser?.roles.some(
    (r) => r.role_type === "super_admin" && r.scope_type === "platform",
  );
  const isOrgAdmin = authUser?.roles.some(
    (r) => r.role_type === "org_admin" && r.scope_type === "organization",
  );

  const assignableRoles = useMemo(() => {
    if (isSuperAdmin) return ALL_ROLE_OPTIONS;
    if (isOrgAdmin)
      return ALL_ROLE_OPTIONS.filter((r) =>
        ["school_admin", "coach", "super_parent", "parent", "player", "sponsor_admin", "sponsor_user"].includes(r.value),
      );
    const isSchoolAdmin = authUser?.roles.some(
      (r) => r.role_type === "school_admin" && r.scope_type === "school",
    );
    if (isSchoolAdmin) return ALL_ROLE_OPTIONS.filter((r) => ["coach", "super_parent", "parent", "player"].includes(r.value));
    return [];
  }, [isSuperAdmin, isOrgAdmin, authUser?.roles]);

  const filterUsers = useMemo(() => {
    if (!hasPinFilter) return undefined;
    return (users: UserListItem[]) =>
      users.filter((u) =>
        u.roles.some((r) => {
          if (r.scope_type === "organization" && activeOrgIds.includes(r.scope_id)) return true;
          if (r.scope_type === "school" && activeSchoolIds.includes(r.scope_id)) return true;
          return false;
        }),
      );
  }, [hasPinFilter, activeOrgIds, activeSchoolIds]);

  return (
    <div className="flex flex-col gap-6" data-page="users-list">
      <PinFilterBar entityType="Organization" />
      <UserManagementPanel
        title="Users"
        subtitle="Manage users and their roles"
        scopeType="platform"
        fetchUsers={api.getUsers}
        assignableRoles={assignableRoles}
        canManage={true}
        showDetailLink={true}
        filterUsers={filterUsers}
        listKey={`${activeOrgIds.join(",")}|${activeSchoolIds.join(",")}`}
        onAddPersona={canImpersonate ? (u: UserListItem) => addPersona({
          userId: u.user_id,
          email: u.email,
          firstName: u.first_name,
          lastName: u.last_name,
          roles: u.roles.map((r) => ({ role_type: r.role_type, scope_type: r.scope_type, scope_id: r.scope_id })),
          addedAt: new Date().toISOString(),
        }) : undefined}
        isPersonaSaved={canImpersonate ? isPersonaSaved : undefined}
      />
    </div>
  );
}
